import React, { useState, useRef } from 'react';
import { Modal, Box, Typography, TextField, Button, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import './AddPoolMemberModal.css';
import CloseIcon from '@mui/icons-material/Close';
import { poolMembersService } from '../../services';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  buildMemberPayload,
  buildMissingFieldsMessage,
  getMissingMemberFields
} from '../memberFormUtils';

function AddPoolMemberModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '', address: '', age: '', occupation: '', mobile: '', duedate: '', startdate: '', pendingFees: 0, feesStatus: 'clear',
    // New measurement fields
    weight: '', chest: '', stomach: '', lowerTier: '', waist: '', arm: '', hip: '', thigh: ''
  });

  const [showMeasurements, setShowMeasurements] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [image, setImage] = useState(null);
  const [imageSize, setImageSize] = useState(0);
  const canvasRef = useRef(null);

  // ... existing image capture code remains the same ...
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          const context = canvas.getContext('2d');
          const maxWidth = 500;
          const scaleSize = maxWidth / img.width;
          canvas.width = maxWidth;
          canvas.height = img.height * scaleSize;
          context.drawImage(img, 0, 0, canvas.width, canvas.height);

          let quality = 0.5;
          let imageDataUrl = canvas.toDataURL('image/jpeg', quality);
          let imageSizeInKB = Math.round((imageDataUrl.length * (3 / 4)) / 1024);

          while (imageSizeInKB > 20 && quality > 0.1) {
            quality -= 0.1;
            imageDataUrl = canvas.toDataURL('image/jpeg', quality);
            imageSizeInKB = Math.round((imageDataUrl.length * (3 / 4)) / 1024);
          }

          setImage(imageDataUrl);
          setImageSize(imageSizeInKB);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    const missingFields = getMissingMemberFields(formData);
    if (missingFields.length > 0) {
      const validationMessage = buildMissingFieldsMessage(missingFields);
      setMessage(validationMessage);
      toast.error(validationMessage, {
        containerId: "pool-toasts"
      });
      return;
    }

    setLoading(true);

    const dataToSend = buildMemberPayload(formData, image, showMeasurements);

    try {
      await poolMembersService.addSwimmer(dataToSend);
      setMessage('User created successfully');
      toast.success('Pool member added successfully!', {
        containerId: "pool-toasts"
      });
      if (onClose) onClose();
      window.dispatchEvent(new Event('pool-members:refresh'));
    } catch (error) {
      console.error('Error during user creation:', error);
      setMessage('An error occurred during user creation: ' + error.message);
      toast.error('Failed to add pool member: ' + error.message, {
        containerId: "pool-toasts"
      });
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <Box className="modal-container">
      <Button
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: "gray"
        }}
      >
        <CloseIcon />
      </Button>
      <Typography variant="h6" component="h2">Add New Member</Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">Personal Information</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required label="Name" name="name" value={formData.name} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required label="Age" name="age" type="number" value={formData.age} onChange={handleChange} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required label="Address" name="address" value={formData.address} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required label="Occupation" name="occupation" value={formData.occupation} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required label="Mobile" name="mobile" type="number" value={formData.mobile} onChange={handleChange} />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              label="Start Date"
              name="startdate"
              type="date"
              value={formData.startdate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              required
              label="Due Date"
              name="duedate"
              type="date"
              value={formData.duedate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Pending Fees Amount"
              name="pendingFees"
              type="number"
              value={formData.pendingFees}
              onChange={handleChange}
              disabled={formData.feesStatus === 'clear'}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Fees Status</InputLabel>
              <Select
                name="feesStatus"
                value={formData.feesStatus}
                onChange={handleChange}
              >
                <MenuItem value="clear">Clear</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Measurements Toggle Button */}
          <Grid item xs={12}>
            <Button 
              variant="outlined" 
              onClick={() => setShowMeasurements(!showMeasurements)}
              fullWidth
            >
              {showMeasurements ? 'Hide Measurements' : 'Add Measurements'}
            </Button>
          </Grid>

          {/* Measurement Fields */}
          {showMeasurements && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Body Measurements</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Weight (kg)" name="weight" type="number" value={formData.weight} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Chest (inch)" name="chest" type="number" value={formData.chest} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Stomach (inch)" name="stomach" type="number" value={formData.stomach} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Lower Tier (inch)" name="lowerTier" type="number" value={formData.lowerTier} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Waist (inch)" name="waist" type="number" value={formData.waist} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Arm (inch)" name="arm" type="number" value={formData.arm} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Hip (inch)" name="hip" type="number" value={formData.hip} onChange={handleChange} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Thigh (inch)" name="thigh" type="number" value={formData.thigh} onChange={handleChange} />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleImageCapture}
              style={{ display: 'none' }}
              id="cameraInput"
            />
            <label htmlFor="cameraInput">
              <Button variant="contained" color="primary" component="span">
                Add Photo (Optional)
              </Button>
            </label>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Photo is optional. Required fields are marked in the form.
            </Typography>
          </Grid>
          {image && (
            <Grid item xs={12}>
              <img src={image} alt="Captured" className="captured-image" />
              <Typography variant="body2">Image Size: {imageSize} KB</Typography>
            </Grid>
          )}
        </Grid>
        <Button type="submit" variant="contained" color="primary" className="submit-button" disabled={loading}>
          {loading ? 'Loading...' : 'Add Member'}
        </Button>
      </form>
      {loading && (
        <div className='login__loading'>
          <svg className="ip" viewBox="0 0 256 128" width="128px" height="64px" xmlns="http://www.w3.org/2000/svg">
            {/* SVG content */}
          </svg>
        </div>
      )}
      {message && <p className='login__message'>{message}</p>}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </Box>
  );

  return isOpen !== undefined ? (
    <Modal open={isOpen} onClose={onClose}>
      {content}
    </Modal>
  ) : content;
}

export default AddPoolMemberModal;